import { useCallback, useEffect, useRef } from 'react';
import { Header, Table } from '@tanstack/react-table';
import { DEFAULT_MIN_COLUMN_SIZE, DEFAULT_MAX_COLUMN_SIZE } from '../utils/constants';

const useGroupResizeHandler = <T>(groupHeaders: Header<T, unknown>[], table: Table<T>) => {
  const cleanupRef = useRef<VoidFunction | null>(null);

  useEffect(() => {
    return () => {
      cleanupRef.current?.();
      table.setColumnSizingInfo(prev => ({ ...prev, isResizingColumn: false }));
    };
  }, [table]);

  return useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();

      cleanupRef.current?.();

      const lastColumnId = groupHeaders[groupHeaders.length - 1]?.column.id;
      table.setColumnSizingInfo(prev => ({ ...prev, isResizingColumn: lastColumnId }));

      const startX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const initialSizes = groupHeaders.map(header => header.getSize());
      const colCount = groupHeaders.length;

      const controller = new AbortController();
      const { signal } = controller;

      let rafId: number | null = null;

      const onMove = (moveEvent: MouseEvent | TouchEvent) => {
        const currentX = 'touches' in moveEvent ? moveEvent.touches[0].clientX : moveEvent.clientX;
        const delta = currentX - startX;
        const perColumnDelta = delta / colCount;

        if (rafId !== null) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          const newSizing: Record<string, number> = {};
          groupHeaders.forEach((header, index) => {
            const minSize = header.column.columnDef.minSize ?? DEFAULT_MIN_COLUMN_SIZE;
            const maxSize = header.column.columnDef.maxSize ?? DEFAULT_MAX_COLUMN_SIZE;
            newSizing[header.column.id] = Math.min(maxSize, Math.max(minSize, initialSizes[index] + perColumnDelta));
          });

          table.setColumnSizing(prev => ({ ...prev, ...newSizing }));
          rafId = null;
        });
      };

      const onEnd = () => {
        if (rafId !== null) cancelAnimationFrame(rafId);
        controller.abort();
        table.setColumnSizingInfo(prev => ({ ...prev, isResizingColumn: false }));
      };

      document.addEventListener('mousemove', onMove, { signal });
      document.addEventListener('mouseup', onEnd, { signal });
      document.addEventListener('touchmove', onMove, { signal, passive: false });
      document.addEventListener('touchend', onEnd, { signal });

      cleanupRef.current = onEnd;
    },
    [groupHeaders, table],
  );
};

export default useGroupResizeHandler;
